import React, { useState, useEffect } from 'react';
import api from '../../api';
import StyleList from './StyleList';
import StyleForm from './StyleForm';
import StyleImportExport from './StyleImportExport';

import '../../styles/styles.css';
import '../../styles/button.css';
import '../../styles/form.css';

const StylesManager = () => {
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [view, setView] = useState('list'); // 'list', 'add', 'edit'

  // Fetch all styles when component loads
  useEffect(() => {
    fetchStyles();
  }, []);

  const fetchStyles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/styles');
      setStyles(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading styles:', err);
      setError('Unable to load styles. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStyle = async (styleData) => {
    try {
      const response = await api.post('/api/admin/styles', styleData);
      setStyles([...styles, response.data]);
      setView('list');
      return { success: true };
    } catch (err) {
      console.error('Error adding style:', err);
      return { 
        success: false, 
        error: err.response?.data?.error || 'Error adding style' 
      };
    }
  };

  const handleUpdateStyle = async (styleData) => {
    try {
      const response = await api.put(`/api/admin/styles/${styleData.id}`, styleData);
      setStyles(styles.map(s => s.id === styleData.id ? response.data : s));
      setView('list');
      setSelectedStyle(null);
      return { success: true };
    } catch (err) {
      console.error('Error updating style:', err);
      return { 
        success: false, 
        error: err.response?.data?.error || 'Error updating style' 
      };
    }
  };

  const handleDeleteStyle = async (styleId) => {
    try {
      await api.delete(`/api/admin/styles/${styleId}`);
      setStyles(styles.filter(s => s.id !== styleId));
      return { success: true };
    } catch (err) {
      console.error('Error deleting style:', err);
      return { 
        success: false, 
        error: err.response?.data?.error || 'Error deleting style' 
      };
    }
  };

  const handleEditClick = (style) => {
    setSelectedStyle(style);
    setView('edit');
  };

  const handleAddClick = () => {
    setSelectedStyle(null);
    setView('add');
  };

  const handleCancelForm = () => {
    setView('list');
    setSelectedStyle(null);
  };

  if (loading) {
    return <div className="panel panel-primary text-center">Loading styles...</div>;
  }

  return (
    <div className="container">
      <div className="panel panel-primary">
        <h1 className="panel-title">Styles Management</h1>
        
        {view === 'list' && (
          <div>
            <div className="mb-4">
              <button className="btn btn-primary" onClick={handleAddClick}>
                Add new style
              </button>
              <StyleImportExport onImportComplete={fetchStyles} styles={styles} />
            </div>
            
            {error && <div className="panel panel-primary" style={{ borderColor: 'var(--color-error)' }}>{error}</div>}
            
            <StyleList 
              styles={styles} 
              onEditClick={handleEditClick}
              onDeleteStyle={handleDeleteStyle}
            />
          </div>
        )}

        {(view === 'add' || view === 'edit') && (
          <StyleForm 
            style={selectedStyle} 
            onSubmit={view === 'add' ? handleAddStyle : handleUpdateStyle}
            onCancel={handleCancelForm}
          />
        )}
      </div>
    </div>
  );
};

export default StylesManager; 